import { useState } from 'react';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useCommissions } from '@/hooks/useCommissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CommissionsTable } from '@/components/admin/commissions/CommissionsTable';
import { Calculator, Users, Banknote } from 'lucide-react';

const AdminCommissionsPage = () => {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const { data: commissionsData, isLoading } = useCommissions(selectedMonth, selectedYear);

  const totalCommission = commissionsData?.reduce((sum, item) => sum + (item.commission_amount || 0), 0) || 0;
  const totalRecords = commissionsData?.length || 0;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-6 w-6" />
                  ค่าคอมมิชชั่นครู
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  แสดงข้อมูลค่าคอมมิชชั่นครูรายเดือน
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-center flex-wrap">
                  {/* Month Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">เดือน:</span>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(Number(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="เดือน" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                          <SelectItem key={month} value={month.toString()}>
                            เดือน {month}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Year Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">ปี:</span>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="ปี" />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">จำนวนรายการ</p>
                      <p className="text-2xl font-bold">{totalRecords.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <Banknote className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">รวมค่าคอมมิชชั่น</p>
                      <p className="text-2xl font-bold text-green-600">
                        ฿{totalCommission.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Data Table */}
            <Card>
              <CardContent className="p-0">
                <CommissionsTable data={commissionsData} isLoading={isLoading} />
              </CardContent>
            </Card>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default AdminCommissionsPage;
